import axios from "axios";
import config from "@/configuration";

export class HttpError extends Error {
  constructor(
    public status: number,
    public description: string,
    /**
     * Field-level problems reported alongside the message, e.g. DHL's
     * `additionalDetails`. Carried separately so callers can render them as a list
     * the operator can work through, rather than one flattened line.
     */
    public details: string[] = [],
  ) {
    super(description);
    this.name = "HttpError";
  }
}

export class UnathorizedError extends Error {
  constructor() {
    super("Unathorized");
    this.name = "UnathorizedError";
  }
}

export type NetworkError = HttpError | UnathorizedError | Error;

// Token getter function, set by ClerkTokenProvider
let clerkTokenGetter: (() => Promise<string | null>) | null = null;

export function setClerkTokenGetter(
  getter: () => Promise<string | null>,
): void {
  clerkTokenGetter = getter;
}

export const client = axios.create({
  baseURL: config.apiUrl,
});

client.interceptors.request.use(async (requestConfig) => {
  if (clerkTokenGetter) {
    const token = await clerkTokenGetter();
    if (token) {
      requestConfig.headers.Authorization = `Bearer ${token}`;
    }
  }
  return requestConfig;
});

client.interceptors.response.use(
  (response) => response,
  (error) => {
    if (axios.isAxiosError(error) && !!error.response) {
      // If status 401 (Unathorized)
      if (error.response.status === 401) {
        return Promise.reject(new UnathorizedError());
      }
      const body = error.response.data as
        | { message?: string | string[]; details?: string[] }
        | undefined;
      // Nest sends validation failures as `message: string[]`; carrier errors send a
      // string plus a `details` array.
      const errorMessage = Array.isArray(body?.message)
        ? body.message.join("; ")
        : (body?.message ?? error.message);
      return Promise.reject(
        new HttpError(
          error.response.status,
          errorMessage,
          Array.isArray(body?.details) ? body.details : [],
        ),
      );
    }

    // Unknown error
    return Promise.reject(new Error("Unknown error"));
  },
);
